import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { formatError, beginSessionEpoch, setSessionExpiredHandler, setWorkspaceScope, setWorkspaceChangedHandler } from "@/lib/api";
import { workspaceScopeOf } from "@/lib/workspaceBinding";
import { requestReread } from "@/lib/reread";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set only when an established session is rejected with 401; never for a failed sign-in.
  const [sessionExpired, setSessionExpired] = useState(false);
  // D10: set when a write of this tab was refused because the current workspace changed in another tab or device.
  const [workspaceChange, setWorkspaceChange] = useState(null);
  const userRef = useRef(null);
  // Identity generation: async results started under an older one must not touch the current user.
  const generation = useRef(0);
  const setUser = useCallback((next) => {
    if (workspaceScopeOf(next) !== workspaceScopeOf(userRef.current) || next?.id !== userRef.current?.id) setWorkspaceChange(null);
    userRef.current = next;
    setWorkspaceScope(next);
    setUserState(next);
  }, []);
  const endSession = useCallback(() => {
    generation.current = beginSessionEpoch();
    localStorage.removeItem("op_token");
    setUser(null);
  }, [setUser]);

  useEffect(() => setSessionExpiredHandler(() => {
    if (!userRef.current) return;
    endSession();
    setSessionExpired(true);
  }), [endSession]);

  useEffect(() => setWorkspaceChangedHandler((detail) => {
    if (!userRef.current) return;
    setWorkspaceChange({ currentWorkspaceId: detail?.current_workspace_id ?? null, pageWorkspaceId: userRef.current.workspace_id || null });
  }), []);

  useEffect(() => {
    const started = generation.current;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (generation.current === started) setUser(data);
      } catch {
        if (generation.current === started) setUser(null);
      } finally {
        if (generation.current === started) setLoading(false);
      }
    })();
  }, [setUser]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const started = generation.current = beginSessionEpoch();
    if (data.access_token) localStorage.setItem("op_token", data.access_token);
    setLoading(false);
    setSessionExpired(false);
    try {
      const me = await api.get("/auth/me");
      if (generation.current === started) setUser(me.data);
    } catch (error) {
      if (generation.current !== started) return data;
      // The server refused the session it just issued: open nothing and drop the refused token.
      if (error?.response?.status === 401) {
        endSession();
        throw Object.assign(new Error("Session refusée par le serveur après la connexion."), { code: "SESSION_REJECTED", response: error.response, cause: error });
      }
      // Network error or 5xx while reading the profile: the login response still granted the session.
      setUser({ id: data.id, email: data.email, name: data.name });
    }
    return data;
  }, [setUser, endSession]);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    generation.current = beginSessionEpoch();
    if (data.access_token) localStorage.setItem("op_token", data.access_token);
    setLoading(false);
    setSessionExpired(false);
    // A new account starts in its personal space (no current workspace on the server).
    setUser({ id: data.id, email: data.email, name: data.name, workspace_id: null, workspace_role: null });
    return data;
  }, [setUser]);

  // Local sign-out always completes; the server call only revokes this device's session.
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    endSession();
  }, [endSession]);

  // Resolves only after the server confirmed the global revocation. Any failure is rethrown:
  // a 401 already ended the local session through the central handler, other errors keep it.
  const logoutEverywhere = useCallback(async () => {
    await api.post("/auth/logout-all");
    endSession();
  }, [endSession]);

  const refresh = useCallback(async () => {
    const started = generation.current;
    const { data } = await api.get("/auth/me");
    if (generation.current === started) setUser(data);
    return data;
  }, [setUser]);

  // D10 choices after a refusal: make this page's workspace current again (the page, its drafts and open forms stay
  // as they are, and its reads are read again in place), or show the current workspace by reloading, as the workspace
  // switcher does.
  const returnToPageWorkspace = useCallback(async () => {
    const started = generation.current;
    const page = userRef.current?.workspace_id || null;
    await api.post("/workspaces/switch", { workspace_id: page });
    const { data } = await api.get("/auth/me");
    if (generation.current !== started) return;
    setUser(data);
    if ((data?.workspace_id || null) === page) { setWorkspaceChange(null); requestReread(); }
  }, [setUser]);
  const showCurrentWorkspace = useCallback(() => { window.location.reload(); }, []);

  const canEdit = !user?.workspace_id || user?.workspace_role !== "viewer";

  return (
    <AuthContext.Provider value={{ user, loading, sessionExpired, login, register, logout, logoutEverywhere, refresh, canEdit, workspaceChange, returnToPageWorkspace, showCurrentWorkspace, role: user?.workspace_id ? user.workspace_role : null, formatError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
